import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Sparkles, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { GRAVEDAD_COLOR } from './shared';
import { DateField } from '@/components/common/DateField';

const API = process.env.REACT_APP_BACKEND_URL;

export function CorrelacionSection({ authHeader }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [filters, setFilters] = useState({ desde: monthAgo, hasta: today, ventana_dias: 30 });
  const [soloSinPrevia, setSoloSinPrevia] = useState(false);

  const [planJobId, setPlanJobId] = useState(null);
  const [planState, setPlanState] = useState('idle');
  const [planResult, setPlanResult] = useState(null);
  const [planError, setPlanError] = useState(null);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [responsableEnvio, setResponsableEnvio] = useState('');
  const [sending, setSending] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['acc-correlacion', filters],
    queryFn: () => axios.get(`${API}/api/accidentes-correlacion/actividades`, { headers: authHeader(), params: filters }).then(r => r.data),
    staleTime: 60_000,
  });

  const { data: objetivosData } = useQuery({
    queryKey: ['objetivos-list-for-plan'],
    queryFn: () => axios.get(`${API}/api/objetivos`, { headers: authHeader() }).then(r => r.data),
    staleTime: 5 * 60_000,
  });
  const responsablesConEmail = (objetivosData?.objetivos || []).filter(o => o.email);

  React.useEffect(() => {
    if (!planJobId || planState !== 'running') return;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/accidentes-correlacion/plan-ia/${planJobId}`, { headers: authHeader() });
        if (cancelled) return;
        if (r.data.status === 'done') {
          setPlanState('done'); setPlanResult(r.data.payload);
        } else if (r.data.status === 'error') {
          setPlanState('error'); setPlanError(r.data.error || 'Error generando plan');
        } else { timer = setTimeout(poll, 4000); }
      } catch { if (!cancelled) timer = setTimeout(poll, 5000); }
    };
    timer = setTimeout(poll, 3000);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [planJobId, planState, authHeader]);

  const startPlan = async () => {
    setPlanState('running'); setPlanResult(null); setPlanError(null); setPlanModalOpen(true);
    try {
      const r = await axios.post(`${API}/api/accidentes-correlacion/plan-ia`, { ...filters }, { headers: authHeader() });
      setPlanJobId(r.data.job_id);
    } catch (e) {
      setPlanState('error'); setPlanError(e?.response?.data?.detail || 'Error iniciando plan');
    }
  };

  const enviarPlan = async () => {
    if (!planResult?.plan_md) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/accidentes-correlacion/enviar-plan`, {
        plan_md: planResult.plan_md, responsable: responsableEnvio || null,
        cc_sps: true, desde: filters.desde, hasta: filters.hasta,
      }, { headers: authHeader() });
      toast.success(`Plan enviado a ${r.data.to.join(', ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando plan');
    } finally { setSending(false); }
  };

  const items = (data?.items || []).filter(i => soloSinPrevia ? i.sin_actividad_previa : true);

  return (
    <div className="space-y-4" data-testid="accidentes-correlacion">
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Desde</Label>
          <DateField value={filters.desde} onChange={v => setFilters({ ...filters, desde: v })} className="w-36" testId="corr-filter-desde" />
        </div>
        <div>
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Hasta</Label>
          <DateField value={filters.hasta} onChange={v => setFilters({ ...filters, hasta: v })} className="w-36" testId="corr-filter-hasta" />
        </div>
        <div>
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Ventana previa</Label>
          <select value={filters.ventana_dias} onChange={e => setFilters({ ...filters, ventana_dias: parseInt(e.target.value, 10) })}
            className="h-8 text-xs px-2 rounded-md bg-transparent border" style={{ borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }} data-testid="corr-filter-ventana">
            {[15, 30, 60, 90].map(v => <option key={v} value={v} style={{ background: '#222' }}>{v} días</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 h-8 px-3 rounded-md text-xs cursor-pointer" style={{ border: '1px solid var(--vz-border-subtle)', color: 'var(--vz-text-secondary)' }}>
          <input type="checkbox" checked={soloSinPrevia} onChange={e => setSoloSinPrevia(e.target.checked)} data-testid="corr-filter-sin-previa" />
          Solo accidentes SIN actividad previa
        </label>
        <Button variant="default" size="sm" className="h-8 text-xs gap-1.5 ml-auto" onClick={startPlan}
          disabled={planState === 'running' || !data || data.sin_actividad_previa === 0} data-testid="corr-btn-plan-ia">
          <Sparkles size={12} />
          {planState === 'running' ? 'Generando plan...' : 'Generar plan de acción IA'}
        </Button>
      </div>

      {planModalOpen && (
        <div data-testid="plan-ia-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.65)' }} onClick={() => planState !== 'running' && setPlanModalOpen(false)}>
          <div className="rounded-xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col"
            style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }} onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-3" style={{ borderColor: 'var(--vz-border-subtle)' }}>
              <div>
                <h3 className="font-bold text-sm" style={{ color: 'var(--vz-text-primary)' }}>Plan de acción preventivo · IA (Claude Sonnet 4.6)</h3>
                <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                  {filters.desde} → {filters.hasta} · ventana previa {filters.ventana_dias}d
                  {planResult && ` · ${planResult.total_centros} centros · ${planResult.total_accidentes_sin_cobertura} accidentes sin cobertura`}
                </p>
              </div>
              <button onClick={() => planState !== 'running' && setPlanModalOpen(false)} disabled={planState === 'running'}
                className="text-xs px-2 py-1 rounded hover:bg-black/10 disabled:opacity-30" style={{ color: 'var(--vz-text-muted)' }} data-testid="plan-ia-close">
                ✕ Cerrar
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-5">
              {planState === 'running' && (
                <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="plan-ia-loading">
                  <Sparkles size={32} className="animate-pulse" style={{ color: 'var(--vz-text-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--vz-text-secondary)' }}>Generando plan personalizado por centro...</p>
                  <p className="text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>Claude está analizando descripciones, riesgos y patrones (40-90 segundos)</p>
                </div>
              )}
              {planState === 'error' && (
                <div className="p-4 rounded" style={{ background: 'rgba(229,57,53,0.08)', border: '1px solid rgba(229,57,53,0.25)' }}>
                  <p className="text-sm font-semibold" style={{ color: '#E53935' }}>Error generando el plan</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--vz-text-secondary)' }}>{planError}</p>
                </div>
              )}
              {planState === 'done' && planResult && (
                <div data-testid="plan-ia-content" className="prose prose-sm max-w-none text-sm leading-relaxed"
                  style={{ color: 'var(--vz-text-primary)', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {planResult.plan_md}
                </div>
              )}
            </div>
            {planState === 'done' && (
              <div className="px-5 py-4 border-t flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-[10px] uppercase" style={{ color: 'var(--vz-text-muted)' }}>Enviar a responsable</Label>
                  <select value={responsableEnvio} onChange={e => setResponsableEnvio(e.target.value)}
                    className="h-8 text-xs px-2 rounded-md bg-transparent border max-w-[260px]"
                    style={{ borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }} data-testid="plan-ia-responsable-select">
                    <option value="" style={{ background: '#222' }}>— Solo SP&S central —</option>
                    {responsablesConEmail.map(o => (
                      <option key={`${o.zona}|${o.responsable}`} value={o.responsable} style={{ background: '#222' }}>
                        {o.responsable} ({o.email})
                      </option>
                    ))}
                  </select>
                  <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>CC: seguridadysalud@valorizasm.com</span>
                </div>
                <Button size="sm" onClick={enviarPlan} disabled={sending} className="h-8 text-xs gap-1.5" data-testid="plan-ia-send-btn">
                  <Mail size={12} /> {sending ? 'Enviando...' : 'Enviar plan por email'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <div className="rounded p-3" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>Accidentes en periodo</div>
            <div className="text-xl font-bold tabular-nums mt-1" style={{ color: 'var(--vz-text-primary)' }}>{data.total}</div>
          </div>
          <div className="rounded p-3" style={{ background: 'rgba(229,57,53,0.08)', border: '1px solid rgba(229,57,53,0.3)' }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: '#E53935' }}>Sin actividad previa ({data.ventana_dias}d)</div>
            <div className="text-xl font-bold tabular-nums mt-1" style={{ color: '#E53935' }}>
              {data.sin_actividad_previa}
              <span className="text-xs ml-1.5 opacity-70">({data.total ? Math.round(data.sin_actividad_previa / data.total * 100) : 0}%)</span>
            </div>
          </div>
          <div className="rounded p-3" style={{ background: 'rgba(0,168,71,0.08)', border: '1px solid rgba(0,168,71,0.25)' }}>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: '#00A847' }}>Con cobertura preventiva</div>
            <div className="text-xl font-bold tabular-nums mt-1" style={{ color: '#00A847' }}>
              {data.total - data.sin_actividad_previa}
              <span className="text-xs ml-1.5 opacity-70">({data.total ? Math.round((data.total - data.sin_actividad_previa) / data.total * 100) : 0}%)</span>
            </div>
          </div>
        </div>
      )}

      {isLoading ? <Skeleton className="h-60 w-full" /> : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
          <Table>
            <TableHeader>
              <TableRow style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
                <TableHead className="text-xs">Fecha</TableHead>
                <TableHead className="text-xs">Trabajador</TableHead>
                <TableHead className="text-xs">Centro</TableHead>
                <TableHead className="text-xs">Gravedad</TableHead>
                <TableHead className="text-xs text-right">OPS previas</TableHead>
                <TableHead className="text-xs text-right">Liderazgo previas</TableHead>
                <TableHead className="text-xs text-right">NM previos</TableHead>
                <TableHead className="text-xs text-center">Cobertura</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-xs text-center py-8" style={{ color: 'var(--vz-text-muted)' }}>Sin accidentes que mostrar.</TableCell></TableRow>
              ) : items.slice(0, 200).map((it, i) => (
                <TableRow key={it.n_referencia || i} style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid={`corr-row-${it.n_referencia || i}`}>
                  <TableCell className="text-xs py-2 font-mono">{it.fecha_accidente}</TableCell>
                  <TableCell className="text-xs py-2">{(it.apellidos || '').toUpperCase()} {(it.nombre || '').toUpperCase()}</TableCell>
                  <TableCell className="text-xs py-2" style={{ color: it.sede_codigo ? 'var(--vz-text-primary)' : '#E53935' }}>
                    {it.sede_label || it.razon_social || '—'}
                  </TableCell>
                  <TableCell className="text-xs py-2">
                    {it.grado_lesion_label ? (
                      <Badge variant="outline" className="text-[9px]" style={{ borderColor: GRAVEDAD_COLOR[it.grado_lesion_label] || '#999', color: GRAVEDAD_COLOR[it.grado_lesion_label] || '#999' }}>
                        {it.grado_lesion_label}
                      </Badge>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="text-xs py-2 text-right tabular-nums" style={{ color: it.ops_previas > 0 ? '#00A847' : 'var(--vz-text-muted)' }}>{it.ops_previas}</TableCell>
                  <TableCell className="text-xs py-2 text-right tabular-nums" style={{ color: it.lid_previas > 0 ? '#00A847' : 'var(--vz-text-muted)' }}>{it.lid_previas}</TableCell>
                  <TableCell className="text-xs py-2 text-right tabular-nums" style={{ color: it.nm_previos > 0 ? '#00A847' : 'var(--vz-text-muted)' }}>{it.nm_previos}</TableCell>
                  <TableCell className="text-xs py-2 text-center">
                    {it.sin_actividad_previa ? (
                      <Badge style={{ background: 'rgba(229,57,53,0.15)', color: '#E53935' }} className="text-[9px]">Sin cobertura</Badge>
                    ) : (
                      <Badge style={{ background: 'rgba(0,168,71,0.15)', color: '#00A847' }} className="text-[9px]">Cubierto</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {items.length > 200 && <div className="text-[10px] py-2 text-center" style={{ color: 'var(--vz-text-muted)' }}>Mostrando 200 de {items.length}. Refina los filtros.</div>}
        </div>
      )}
    </div>
  );
}
